import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Badge from 'assets/salesNew/offer/badge.svg';
import DateIcon from 'assets/salesNew/offer/date.svg';
import LevinIcon from 'assets/salesNew/offer/levin.svg';
import StarIcon from 'assets/salesNew/offer/star.svg';
import WifiIcon from 'assets/salesNew/offer/wifi.svg';
import { getOffers } from 'features/newsales/newsalesActions';
import LeadSourceImage from 'pages/settings/salesNewSettings/components/LeadSourceImage';

import { useLeadForm } from '../LeadFormContext';
import {
  BadgeIcon,
  CardHeader,
  Container,
  DateImg,
  ErrorMessage,
  FeatureTag,
  FeatureTagImg,
  FooterText,
  InfoDescription,
  InfoHeader,
  InfoSide,
  LevinImg,
  LoadingSpinner,
  MainInfoContent,
  OfferCard,
  OfferCardBig,
  OfferImageBig,
  OfferImageWrapper,
  OffersGrid,
  OffersGridBig,
  PriceBox,
  PriceTitle,
  PriceValue,
  ProductBadge,
  StarImg,
  SubTitle,
  TagGroup,
  Title,
  UnitsInfo,
  ValidityBox,
} from './OfferInfo.styles';

const OfferInfo = ({ onValidationChange, shouldValidate }) => {
  const dispatch = useDispatch();
  const { formData, updateFormData } = useLeadForm();
  const [selectedOffers, setSelectedOffers] = useState(formData.offerIds || []);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const fetchOffers = async () => {
      setLoading(true);
      try {
        let allOffers = [];
        let page = 1;
        const limit = 12;
        let hasMore = true;

        while (hasMore) {
          const params = {
            status: 'enabled',
            page,
            limit,
            productId: formData.productIds || [],
          };
          const result = await dispatch(getOffers(params));

          const responseData = result.payload?.data || [];
          allOffers = [...allOffers, ...responseData];

          hasMore = responseData.length === limit;
          page += 1;
        }

        setOffers(allOffers);
      } catch (error) {
        console.error('Error fetching offers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, [dispatch, formData.productIds]);

  const handleToggle = (id) => {
    setSelectedOffers((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
    setShowError(false);
  };

  const isSimpleOffer = (offer) => {
    const hasDetailedFields =
      (offer.description && offer.description.trim() !== '') ||
      (offer.features && offer.features.length > 0) ||
      (offer.category && offer.category.trim() !== '');

    return !hasDetailedFields;
  };

  const simpleOffers = offers.filter(isSimpleOffer);
  const detailedOffers = offers.filter((offer) => !isSimpleOffer(offer));

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  useEffect(() => {
    updateFormData({ offerIds: selectedOffers });
  }, [selectedOffers]);

  useEffect(() => {
    const isValid = selectedOffers.length > 0;
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [selectedOffers]);

  useEffect(() => {
    if (shouldValidate && selectedOffers.length === 0) {
      setShowError(true);
    }
  }, [shouldValidate, selectedOffers]);

  return (
    <Container>
      <Title>Select Offers</Title>
      <SubTitle>Select Offer</SubTitle>
      <MainInfoContent>
        {loading ? (
          <LoadingSpinner>Loading offers...</LoadingSpinner>
        ) : (
          <>
            {simpleOffers.length > 0 && (
              <OffersGrid>
                {simpleOffers.map((offer) => (
                  <OfferCard
                    key={offer.id}
                    isActive={selectedOffers.includes(offer.id)}
                    onClick={() => handleToggle(offer.id)}
                  >
                    <BadgeIcon src={Badge} />
                    {offer.image && (
                      <OfferImageWrapper>
                        <LeadSourceImage imagePath={offer.image} alt={offer.name} size={30} />
                      </OfferImageWrapper>
                    )}
                    <CardHeader title={offer.name}>{offer.name}</CardHeader>

                    <PriceBox>
                      <PriceTitle>Total Price</PriceTitle>
                      <PriceValue>
                        {offer.price} <span>{offer.currency || 'AMD'}</span>
                      </PriceValue>
                      {offer.count && (
                        <UnitsInfo>
                          <StarImg src={StarIcon} /> {offer.count} Units Included
                        </UnitsInfo>
                      )}
                    </PriceBox>

                    {(offer.validFrom || offer.validTo) && (
                      <ValidityBox>
                        <DateImg src={DateIcon} className="calendar-icon" />
                        Valid:{' '}
                        <span>
                          {formatDate(offer.validFrom)} - {formatDate(offer.validTo)}
                        </span>
                      </ValidityBox>
                    )}

                    <FooterText>
                      <LevinImg src={LevinIcon} /> Premium Offer
                    </FooterText>
                  </OfferCard>
                ))}
              </OffersGrid>
            )}

            {detailedOffers.length > 0 && (
              <OffersGridBig>
                {detailedOffers.map((offer) => {
                  const isActive = selectedOffers.includes(offer.id);
                  return (
                    <OfferCardBig
                      key={offer.id}
                      isActive={isActive}
                      onClick={() => handleToggle(offer.id)}
                    >
                      <OfferCard className="offer-flex" isActive={isActive}>
                        <BadgeIcon src={Badge} />
                        <CardHeader className="card-header" title={offer.name}>
                          {offer.name}
                        </CardHeader>

                        <PriceBox>
                          <PriceTitle>Total Price</PriceTitle>
                          <PriceValue>
                            {offer.oldPrice || offer.price}{' '}
                            <span>{offer.currency || 'AMD'}</span>
                          </PriceValue>
                          {offer.oldPrice && offer.oldPrice !== offer.price && (
                            <>
                              <PriceTitle className="discount-label">
                                % DISCOUNTED PRICE
                              </PriceTitle>
                              <PriceValue className="discount-price">
                                {offer.price} <span>{offer.currency || 'AMD'}</span>
                              </PriceValue>
                            </>
                          )}
                          {offer.count && (
                            <UnitsInfo className="units-info">
                              <StarImg src={StarIcon} /> {offer.count} Units Included
                            </UnitsInfo>
                          )}
                        </PriceBox>

                        {(offer.validFrom || offer.validTo) && (
                          <ValidityBox className="validity-box">
                            <DateImg src={DateIcon} className="calendar-icon" />
                            Valid:{' '}
                            <span>
                              {formatDate(offer.validFrom)} - {formatDate(offer.validTo)}
                            </span>
                          </ValidityBox>
                        )}

                        <FooterText className="premium-offer">
                          <LevinImg src={LevinIcon} /> Premium Offer
                        </FooterText>
                      </OfferCard>

                      <InfoSide>
                        {offer.description && (
                          <InfoDescription title={offer.description}>
                            {offer.description}
                          </InfoDescription>
                        )}

                        {offer.features && offer.features.length > 0 && (
                          <div>
                            <InfoHeader>PREMIUM FEATURES</InfoHeader>
                            <TagGroup>
                              {offer.features.slice(0, 4).map((feature, index) => (
                                <FeatureTag key={index}>
                                  <FeatureTagImg>
                                    <LevinImg className="wifi-img" src={WifiIcon} />
                                  </FeatureTagImg>
                                  {feature}
                                </FeatureTag>
                              ))}
                            </TagGroup>
                          </div>
                        )}

                        {offer.productName && (
                          <ProductBadge>
                            Product <strong>{offer.productName}</strong>
                          </ProductBadge>
                        )}

                        {offer.image && (
                          <OfferImageBig>
                            <LeadSourceImage
                              imagePath={offer.image}
                              alt={offer.name}
                              size={90}
                            />
                          </OfferImageBig>
                        )}
                      </InfoSide>
                    </OfferCardBig>
                  );
                })}
              </OffersGridBig>
            )}
            {showError && selectedOffers.length === 0 && (
              <ErrorMessage>Please select at least one offer to continue</ErrorMessage>
            )}
          </>
        )}
      </MainInfoContent>
    </Container>
  );
};

export default OfferInfo;
